/**
 * webPush.js — helper functions for browser-native push subscription.
 *
 * Flow:
 *   1. fetch /api/push/public-key to get the VAPID key
 *   2. ask the user for Notification permission
 *   3. subscribe via the active service worker
 *   4. POST the PushSubscription JSON to /api/push/subscribe
 */
import { api } from "./api";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const bytes = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
  return bytes;
}

export function pushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export function permissionState() {
  if (!("Notification" in window)) return "unsupported";
  return Notification.permission; // 'default' | 'granted' | 'denied'
}

export async function subscribeToPush() {
  if (!pushSupported()) throw new Error("Push not supported in this browser");

  const reg = await navigator.serviceWorker.ready;

  if (Notification.permission !== "granted") {
    const result = await Notification.requestPermission();
    if (result !== "granted") throw new Error("Notification permission denied");
  }

  // Reuse existing subscription if already present
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    const { data } = await api.get("/push/public-key");
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(data.public_key),
    });
  }

  // Persist on the backend (idempotent — endpoint is the unique key).
  // If the server rejects, roll back the browser-level subscription so
  // client + server state stay in sync.
  try {
    await api.post("/push/subscribe", sub.toJSON());
  } catch (err) {
    try { await sub.unsubscribe(); } catch { /* best-effort */ }
    throw new Error(err?.response?.data?.detail || "Server rejected subscription");
  }
  return sub;
}

export async function unsubscribeFromPush() {
  if (!pushSupported()) return;
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (sub) {
    try {
      await api.post("/push/unsubscribe", { endpoint: sub.endpoint });
    } catch { /* best-effort */ }
    await sub.unsubscribe();
  }
}
